import { apiSlice } from '../api/apiSlice';
import {
  FloorplanActivityEntry,
  FloorplanAutoAssignPreview,
  FloorplanBatchAssignmentInput,
  FloorplanEmployee,
  FloorplanGeometryInput,
  FloorplanLayoutDetail,
  FloorplanLayoutSummary,
  FloorplanTeamMovePreview,
  FloorplanVersion,
} from '../../types/floorplan';

export const floorplanApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    saveFloorplanGeometry: builder.mutation<
      { levelId: string; updatedAt: string },
      { layoutId: string; levelId: string; geometry: FloorplanGeometryInput }
    >({
      query: ({ levelId, geometry }) => ({
        url: `/admin/floorplan/levels/${levelId}/geometry`,
        method: 'PUT',
        body: geometry,
      }),
      transformResponse: (response: { success: boolean; data: { levelId: string; updatedAt: string } }) => response.data,
      invalidatesTags: (result, _error, { layoutId }) => result ? [
        { type: 'Floorplan', id: layoutId },
        { type: 'Floorplan', id: 'LIST' },
        { type: 'FloorplanActivity', id: 'LIST' },
      ] : [],
    }),
    getFloorplanRoster: builder.query<FloorplanEmployee[], void>({
      query: () => '/admin/floorplan/roster',
      transformResponse: (response: { success: boolean; data: FloorplanEmployee[] }) => response.data,
      providesTags: [{ type: 'FloorplanRoster', id: 'LIST' }],
    }),
    getFloorplanLayouts: builder.query<FloorplanLayoutSummary[], void>({
      query: () => '/admin/floorplan/layouts',
      transformResponse: (response: { success: boolean; data: FloorplanLayoutSummary[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Floorplan' as const, id })),
              { type: 'Floorplan', id: 'LIST' },
            ]
          : [{ type: 'Floorplan', id: 'LIST' }],
    }),
    getFloorplanLayout: builder.query<FloorplanLayoutDetail, { layoutId: string; versionId?: string }>({
      query: ({ layoutId, versionId }) => ({
        url: `/admin/floorplan/layouts/${layoutId}`,
        params: versionId ? { versionId } : undefined,
      }),
      transformResponse: (response: { success: boolean; data: FloorplanLayoutDetail }) => response.data,
      providesTags: (result, _error, { layoutId, versionId }) => [
        { type: 'Floorplan', id: layoutId },
        { type: 'Floorplan', id: `layout-${layoutId}-${versionId || 'active'}` },
      ],
    }),
    createFloorplanVersion: builder.mutation<
      FloorplanVersion,
      { layoutId: string; name: string; description?: string; cloneFromVersionId?: string }
    >({
      query: (body) => ({
        url: '/admin/floorplan/versions',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: FloorplanVersion }) => response.data,
      invalidatesTags: (_result, _error, { layoutId }) => [
        { type: 'Floorplan', id: layoutId },
        { type: 'Floorplan', id: 'LIST' },
        { type: 'FloorplanActivity', id: 'LIST' },
      ],
    }),
    activateFloorplanVersion: builder.mutation<{ id: string }, { versionId: string; layoutId: string }>({
      query: ({ versionId }) => ({
        url: `/admin/floorplan/versions/${versionId}/activate`,
        method: 'PATCH',
      }),
      transformResponse: (response: { success: boolean; data: { id: string } }) => response.data,
      invalidatesTags: (_result, _error, { layoutId }) => [
        { type: 'Floorplan', id: layoutId },
        { type: 'Floorplan', id: 'LIST' },
        { type: 'FloorplanActivity', id: 'LIST' },
      ],
    }),
    saveFloorplanAssignment: builder.mutation<
      { deskId: string; employeeId: string },
      { versionId: string; deskId: string; employeeId: string; layoutId: string }
    >({
      query: ({ versionId, deskId, employeeId }) => ({
        url: '/admin/floorplan/assignments',
        method: 'PUT',
        body: { versionId, deskId, employeeId },
      }),
      transformResponse: (response: { success: boolean; data: { deskId: string; employeeId: string } }) => response.data,
      invalidatesTags: () => [
        { type: 'FloorplanActivity', id: 'LIST' },
      ],
    }),
    clearFloorplanAssignment: builder.mutation<{ deskId: string }, { versionId: string; deskId: string; layoutId: string }>({
      query: ({ versionId, deskId }) => ({
        url: `/admin/floorplan/assignments/${deskId}`,
        method: 'DELETE',
        params: { versionId },
      }),
      transformResponse: (response: { success: boolean; data: { deskId: string } }) => response.data,
      invalidatesTags: () => [
        { type: 'FloorplanActivity', id: 'LIST' },
      ],
    }),
    clearFloorplanLevelAssignments: builder.mutation<
      { clearedCount: number; levelId: string },
      { versionId: string; levelId: string; layoutId: string }
    >({
      query: ({ versionId, levelId }) => ({
        url: `/admin/floorplan/levels/${levelId}/assignments`,
        method: 'DELETE',
        params: { versionId },
      }),
      transformResponse: (
        response: { success: boolean; data: { clearedCount: number; levelId: string } }
      ) => response.data,
      invalidatesTags: () => [
        { type: 'FloorplanActivity', id: 'LIST' },
      ],
    }),
    applyFloorplanBatchAssignments: builder.mutation<
      any,
      {
        versionId: string;
        assignments: FloorplanBatchAssignmentInput[];
        activityType?: string;
        activityDetails?: Record<string, unknown>;
      }
    >({
      query: (body) => ({
        url: '/admin/floorplan/assignments/batch',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      invalidatesTags: () => [{ type: 'FloorplanActivity', id: 'LIST' }],
    }),
    previewNearbyTeamMove: builder.mutation<
      FloorplanTeamMovePreview,
      { versionId: string; teamName: string; targetDeskId: string; sourceLevelId?: string | null }
    >({
      query: ({ teamName, ...body }) => ({
        url: `/admin/floorplan/teams/${encodeURIComponent(teamName)}/select-nearby`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: FloorplanTeamMovePreview }) => response.data,
    }),
    previewFloorplanAutoAssign: builder.mutation<
      FloorplanAutoAssignPreview,
      { versionId: string; mode: 'team' | 'all'; teamName?: string | null; levelId?: string | null }
    >({
      query: (body) => ({
        url: '/admin/floorplan/auto-assign/preview',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: FloorplanAutoAssignPreview }) => response.data,
    }),
    applyFloorplanAutoAssign: builder.mutation<
      { preview: FloorplanAutoAssignPreview; result: any },
      { versionId: string; mode: 'team' | 'all'; teamName?: string | null; levelId?: string | null }
    >({
      query: (body) => ({
        url: '/admin/floorplan/auto-assign/apply',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: { preview: FloorplanAutoAssignPreview; result: any } }) => response.data,
      invalidatesTags: () => [{ type: 'FloorplanActivity', id: 'LIST' }],
    }),
    getFloorplanActivity: builder.query<FloorplanActivityEntry[], { layoutId?: string; limit?: number } | void>({
      query: (arg) => ({
        url: '/admin/floorplan/activity',
        params: arg || undefined,
      }),
      transformResponse: (response: { success: boolean; data: FloorplanActivityEntry[] }) => response.data,
      providesTags: [{ type: 'FloorplanActivity', id: 'LIST' }],
    }),
  }),
});

export const {
  useSaveFloorplanGeometryMutation,
  useActivateFloorplanVersionMutation,
  useApplyFloorplanAutoAssignMutation,
  useApplyFloorplanBatchAssignmentsMutation,
  useClearFloorplanLevelAssignmentsMutation,
  useClearFloorplanAssignmentMutation,
  useCreateFloorplanVersionMutation,
  useGetFloorplanActivityQuery,
  useGetFloorplanLayoutQuery,
  useGetFloorplanLayoutsQuery,
  useGetFloorplanRosterQuery,
  usePreviewFloorplanAutoAssignMutation,
  usePreviewNearbyTeamMoveMutation,
  useSaveFloorplanAssignmentMutation,
} = floorplanApiSlice;
